import { get, debounce } from "lodash";
import { setSelectedSubTab } from "../../components/organisms/BreadCrumb";
import { useState, useCallback, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import {
  chevronLeftIcon,
  menuDeleteIcon,
  menuDuplicateIcon,
  menuEditIcon,
  reloadIcon,
} from "../../assets/icons";
import {
  EmptyLayout,
  Footer,
  SearchBar,
  SuggestionBar,
  Table,
} from "../../components/molecules";
import { PopOver } from "../../components/atoms";
import {
  POPOVER_BUTTONS,
  FOOTER_CONSTANTS,
  STATUS,
  TABS,
  EMPTY_STRING,
  SEARCH_DEBOUNCE_TIME,
} from "../../constants";
import DeleteConfirmationModal from "../../components/organisms/AppModals/DeleteConfirmationModal";
import { DRAWERS } from "../../components/organisms/AppDrawers/drawers.constants";
import {
  deleteBackgroundTask,
  selectBackgroundTasks,
  selectSelectedDataModel,
  selectBackgroundTaskFilters,
  selectSelectedDataModelId,
  createBackgroundTask,
  setSelectedBackgroundTask,
  fetchServices,
} from "./index";
import { actions as drawerActions } from "../../components/organisms/Drawer/drawers.reducers";
import { actions } from "./dataModels.reducers";
import { actions as modalActions } from "../../components/organisms/Modal/modal.reducers";
import { actions as breadCrumbActions } from "../../components/organisms/BreadCrumb/breadCrumb.reducers";
import { MODALS } from "../../components/organisms/AppModals/modals.constants";
import { renderNoDataModelLayout } from "../../helpers";
import { getDataModelSuggestionMessage, getRegion } from "./services.helpers";
import Spinner, { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { updateBackgroundTask, selectDataModelRegions } from "../DataModels";

const { EDIT, DELETE, DUPLICATE } = POPOVER_BUTTONS;
const { MANAGE_BACKGROUND_TASK_DRAWER } = DRAWERS;
const ONE = 1;
const NAVIGATE_BACK = -1;
const BACKGROUND_TASKS = "background-tasks";
const { INACTIVE } = STATUS;
const { SETTINGS } = TABS;

const { setSelectedDataModelId, setBackgroundTaskFilters } = actions;
const { setSelectedDrawer } = drawerActions;
const { setSelectedModal, setItemToBeDeleted } = modalActions;
const { setSelectedTab } = breadCrumbActions;

const { BACKGROUND_TASKS_TITLE, BACKGROUND_TASKS_SUBTITLE } = FOOTER_CONSTANTS;
const { DELETE_BACKGROUND_TASK, FETCH_BACKGROUND_TASKS } = SPINNER_NAMES;

const BackgroundTasks = () => {
  const { dataModelId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: backgroundTasks, total: totalBackgroundTasks } = useSelector(selectBackgroundTasks);
  const filters = useSelector(selectBackgroundTaskFilters);
  const selectedDataModelId = useSelector(selectSelectedDataModelId);
  const selectedDataModel = useSelector(selectSelectedDataModel);
  const dataModelRegions = useSelector(selectDataModelRegions);

  const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState(false);
  const [shouldShowSuggestionBar, setShouldShowSuggestionBar] = useState(false);

  useEffect(() => {
    return () => dispatch(setBackgroundTaskFilters());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDataModel) {
      dispatch(setSelectedTab(selectedDataModel));
      const showSuggestionBar = Boolean(
        selectedDataModel.status === INACTIVE ||
          (dataModelRegions &&
            selectedDataModel.region !== getRegion(dataModelRegions))
      );
      setShouldShowSuggestionBar(showSuggestionBar);
    }
  }, [dispatch, selectedDataModel, dataModelRegions]);

  useEffect(() => {
    if (dataModelId && !selectedDataModelId)
      dispatch(setSelectedDataModelId(dataModelId));
  }, [dispatch, dataModelId, selectedDataModelId]);

  const getBackgroundTaskName = useCallback(
    (name, copy = 0) => {
      const duplicateBackgroundTaskName = copy ? `${name}_${copy}` : name;
      const isDuplicateBackgroundTaskName = backgroundTasks.some(
        (a) => a.name === duplicateBackgroundTaskName
      );
      return isDuplicateBackgroundTaskName
        ? getBackgroundTaskName(name, copy + ONE)
        : duplicateBackgroundTaskName;
    },
    [backgroundTasks]
  );

  const handleOnDuplicate = useCallback(
    (row) => {
      const { ...restBackgroundTaskKey } = row;
      const backgroundTask = {
        ...restBackgroundTaskKey,
        name: getBackgroundTaskName(row.name),
      };
      dispatch(createBackgroundTask(backgroundTask));
    },
    [dispatch, getBackgroundTaskName]
  );

  const popOverOptions = (row) => [
    {
      name: EDIT,
      icon: menuEditIcon,
      handleOnClick: () => {
        dispatch(setSelectedBackgroundTask(row));
        dispatch(setSelectedDrawer(MANAGE_BACKGROUND_TASK_DRAWER));
      },
    },
    {
      name: DUPLICATE,
      icon: menuDuplicateIcon,
      handleOnClick: (row) => handleOnDuplicate(row),
    },
    {
      name: DELETE,
      icon: menuDeleteIcon,
      classes: "border-t border-gray-600",
      handleOnClick: () => {
        dispatch(setSelectedModal(MODALS.DELETE_MODAL));
        dispatch(setItemToBeDeleted(row));
        setShouldShowDeleteModal(true);
      },
    },
  ];

  const buttonFormatter = (row) => (
    <div onClick={(e) => e.stopPropagation()}>
      <PopOver
        options={popOverOptions(row)}
        classes="right-full -bottom-3"
        data={row}
      />
    </div>
  );
  const statusFormatter = (row) => (
    <label className="inline-flex cursor-pointer mr-auto md:mr-0 ml-auto mb-5">
      <div className="inline-flex" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          name="status"
          checked={row.isEnabled}
          role="switch"
          onChange={(e) => {
            dispatch(
              updateBackgroundTask({
                id: row.id,
                backgroundTaskDetails: { isEnabled: e.target.checked },
              })
            );
          }}
          className="hidden peer"
        />
        <div className="w-11 h-6 relative bg-gray-700 rounded-full peer-checked:after:translate-x-full peer-checked:after:border-white  after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-emerald-500"></div>
      </div>
    </label>
  );

  const serviceFormatter = (row) => (
    <Link
      to={`/data-models/${dataModelId}/services/${row.serviceId}/details`}
      className="text-indigo-200 hover:text-indigo-500"
      onClick={(e) => {
        dispatch(setSelectedSubTab(BACKGROUND_TASKS));
        e.stopPropagation();
      }}
    >
      {get(row, "services.name", EMPTY_STRING)}
    </Link>
  );

  const columns = [
    {
      dataField: "name",
      text: "Name",
    },
    {
      text: "Service",
      formatter: serviceFormatter,
    },
    {
      text: "Status",
      className: "text-center",
      formatter: statusFormatter,
    },
    {
      text: "Should Repeat",
      className: "text-center",
      formatter: (row) => `${row.shouldRepeat}`,
    },
    {
      width: 70,
      formatter: buttonFormatter,
    },
  ];

  const handleDeleteBackgroundTask = useCallback(
    (backgroundTask) => {
      dispatch(deleteBackgroundTask(backgroundTask.id)).then((res) => {
        if (res.payload) {
          dispatch(fetchServices({ id: dataModelId }));
          dispatch(setItemToBeDeleted(null));
          dispatch(setSelectedModal(null));
          dispatch(setItemToBeDeleted(null));
          setShouldShowDeleteModal(false);
        }
      });
    },
    [dispatch, dataModelId]
  );

  const handleOnSearch = debounce(
    (value) => dispatch(setBackgroundTaskFilters({ ...filters, name: value })),
    SEARCH_DEBOUNCE_TIME
  );

  const handleShouldShowDrawer = useCallback(
    () => dispatch(setSelectedDrawer(MANAGE_BACKGROUND_TASK_DRAWER)),
    [dispatch]
  );

  const renderBackgroundTasksLayout = () => {
    return backgroundTasks.length ? (
      <>
        <Table
          data={backgroundTasks}
          columns={columns}
          onRowClick={handleOnRowClick}
        />
        {shouldShowDeleteModal && (
          <DeleteConfirmationModal
            handleDelete={handleDeleteBackgroundTask}
            setShouldShowDeleteModal={setShouldShowDeleteModal}
            title="Delete Background Task"
            spinner={DELETE_BACKGROUND_TASK}
          />
        )}
      </>
    ) : (
      <EmptyLayout
        title="No Background Task"
        subTitle="You can get started by creating a new background task."
        buttonText="Create New Background Task"
        image={reloadIcon}
        handleOnClick={handleShouldShowDrawer}
      />
    );
  };

  const handleOnRowClick = (row) => {
    dispatch(setSelectedBackgroundTask(row));
    dispatch(setSelectedDrawer(MANAGE_BACKGROUND_TASK_DRAWER));
  };

  return (
    <div className="content scrollbar overflow-auto p-6 flex-1 flex flex-col">
      <Helmet>
        <title>BackgroundTasks | TavaJS</title>
      </Helmet>
      <div className="flex-col xs:flex-row flex justify-between mb-4 gap-4">
        <div className="flex flex-row">
          <button
            className="bg-gray-800 px-2 h-10 rounded-md hover:bg-gray-700/60 mr-1"
            onClick={() => navigate(NAVIGATE_BACK)}
          >
            <img src={chevronLeftIcon} width="24" alt="chevron-left-icon" />
          </button>
          {selectedDataModel && !!totalBackgroundTasks && (
            <SearchBar placeholder="Search" handleOnSearch={handleOnSearch} filters={filters}/>
          )}
        </div>
        {selectedDataModel && (
          <button
            type="button"
            className="btn btn-primary w-full xs:w-auto"
            onClick={() => handleShouldShowDrawer()}
          >
            Create Background Task
          </button>
        )}
      </div>
      {selectedDataModel && shouldShowSuggestionBar && (
        <SuggestionBar
          text={getDataModelSuggestionMessage(
            selectedDataModel,
            dataModelRegions
          )}
          link={SETTINGS}
          handleOnClick={() => navigate(`/data-models/${dataModelId}/settings`)}
          classes="text-red-600"
        />
      )}
      <div className="justify-between flex flex-col flex-1">
        <Spinner name={FETCH_BACKGROUND_TASKS}>
          {selectedDataModel
            ? renderBackgroundTasksLayout()
            : renderNoDataModelLayout(navigate)}
        </Spinner>
        {selectedDataModel &&
        <Footer
          title={BACKGROUND_TASKS_TITLE}
          subtitle={BACKGROUND_TASKS_SUBTITLE}
        />
        }
      </div>
    </div>
  );
};

export default BackgroundTasks;
